import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Loader2,
  LogOut,
  RefreshCw,
  ShieldCheck,
  Users,
  Mail,
  Check,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import Logo from "@/components/Logo";
import {
  adminLogin,
  adminGetContacts,
  adminGetLeads,
  adminUpdateStatus,
} from "@/lib/api";
import { Button } from "@/components/ui/button";

const TOKEN_KEY = "batear_admin_token";

function toCsv(rows, columns) {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) =>
    columns.map((c) => escape(r[c.key])).join(",")
  );
  return [header, ...body].join("\n");
}

function downloadCsv(filename, rows, columns) {
  const csv = toCsv(rows, columns);
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const LEAD_COLUMNS = [
  { key: "created_at", label: "Date" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "need", label: "Needs Help With" },
  { key: "status", label: "Status" },
];

const CONTACT_COLUMNS = [
  { key: "created_at", label: "Date" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "need", label: "Needs Help With" },
  { key: "message", label: "Message" },
  { key: "status", label: "Status" },
];

const STATUS_STYLES = {
  new: "bg-gold/15 text-gold border-gold/40",
  contacted: "bg-navy/5 text-navy border-navy/25",
  archived: "bg-ink/5 text-ink/40 border-ink/15",
};

function StatusBadge({ status }) {
  const s = status || "new";
  return (
    <span
      data-testid={`status-badge-${s}`}
      className={`inline-block px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border ${STATUS_STYLES[s] || STATUS_STYLES.new}`}
    >
      {s}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminDashboard() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem(TOKEN_KEY) || ""
  );
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [updatingId, setUpdatingId] = useState("");

  const loadData = useCallback(async (t) => {
    setLoading(true);
    setError("");
    try {
      const [leadRows, contactRows] = await Promise.all([
        adminGetLeads(t),
        adminGetContacts(t),
      ]);
      setLeads(leadRows);
      setContacts(contactRows);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Could not load data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError("");
    try {
      const { token: t } = await adminLogin(password);
      sessionStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword("");
    } catch {
      setAuthError("Invalid password. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
    setContacts([]);
  };

  const setStatus = async (collection, id, status) => {
    setUpdatingId(id);
    try {
      await adminUpdateStatus(token, collection, id, status);
      const apply = (rows) =>
        rows.map((r) => (r.id === id ? { ...r, status } : r));
      if (collection === "leads") setLeads(apply);
      else setContacts(apply);
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update status. Please try again.");
    } finally {
      setUpdatingId("");
    }
  };

  /* -------------------- Login screen -------------------- */
  if (!token) {
    return (
      <div className="min-h-screen bg-navy-deep flex flex-col">
        <header className="max-w-5xl w-full mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" data-testid="admin-home-link">
            <Logo dark markSize={32} />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/70 hover:text-gold text-sm"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center px-5 pb-20">
          <form
            onSubmit={handleLogin}
            data-testid="admin-login-form"
            className="w-full max-w-sm bg-white/5 border border-gold/25 p-8 backdrop-blur"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-gold" size={22} />
              <h1 className="font-display text-white text-2xl font-semibold">
                Admin Access
              </h1>
            </div>
            <p className="mt-2 text-white/60 text-sm">
              Enter the admin password to view leads and enquiries.
            </p>

            <label
              htmlFor="admin-password"
              className="block mt-6 text-xs uppercase tracking-[0.2em] text-gold"
            >
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              className="mt-2 w-full bg-navy-deep/60 border border-white/15 focus:border-gold outline-none text-white px-4 py-3 text-sm"
              placeholder="Admin password"
            />

            {authError && (
              <p
                data-testid="admin-login-error"
                className="mt-3 text-sm text-red-400"
              >
                {authError}
              </p>
            )}

            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={authLoading}
              className="mt-6 w-full bg-gold text-navy-deep hover:bg-gold/90 font-semibold rounded-none h-11"
            >
              {authLoading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </main>
      </div>
    );
  }

  /* -------------------- Dashboard -------------------- */
  const allRows = tab === "leads" ? leads : contacts;
  const columns = tab === "leads" ? LEAD_COLUMNS : CONTACT_COLUMNS;
  const archivedCount = allRows.filter(
    (r) => (r.status || "new") === "archived"
  ).length;
  const rows = showArchived
    ? allRows
    : allRows.filter((r) => (r.status || "new") !== "archived");

  return (
    <div className="min-h-screen bg-paper">
      <header className="bg-navy-deep text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="admin-home-link">
            <Logo dark markSize={30} />
          </Link>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => loadData(token)}
              data-testid="admin-refresh-button"
              variant="outline"
              className="rounded-none border-white/20 bg-transparent text-white/80 hover:text-gold hover:bg-white/5"
            >
              <RefreshCw size={15} className="mr-2" /> Refresh
            </Button>
            <Button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              variant="outline"
              className="rounded-none border-white/20 bg-transparent text-white/80 hover:text-gold hover:bg-white/5"
            >
              <LogOut size={15} className="mr-2" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gold">
              Private
            </div>
            <h1 className="mt-2 font-display font-bold text-navy text-3xl sm:text-4xl">
              Leads Dashboard
            </h1>
          </div>
          <Button
            onClick={() =>
              downloadCsv(
                tab === "leads" ? "batear-leads.csv" : "batear-contacts.csv",
                rows,
                columns
              )
            }
            data-testid="admin-export-csv-button"
            className="bg-navy text-white hover:bg-navy/90 rounded-none"
          >
            <Download size={15} className="mr-2" /> Export CSV
          </Button>
        </div>

        {/* Stat cards */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            data-testid="admin-stat-leads"
            className="border border-navy/10 bg-white p-5 flex items-center gap-4"
          >
            <div className="w-11 h-11 flex items-center justify-center bg-gold/15 text-gold">
              <Download size={20} />
            </div>
            <div>
              <div className="font-display text-3xl font-bold text-navy">
                {leads.length}
              </div>
              <div className="text-ink/50 text-sm">Guide downloads (leads)</div>
            </div>
          </div>
          <div
            data-testid="admin-stat-contacts"
            className="border border-navy/10 bg-white p-5 flex items-center gap-4"
          >
            <div className="w-11 h-11 flex items-center justify-center bg-gold/15 text-gold">
              <Mail size={20} />
            </div>
            <div>
              <div className="font-display text-3xl font-bold text-navy">
                {contacts.length}
              </div>
              <div className="text-ink/50 text-sm">Consultation enquiries</div>
            </div>
          </div>
        </div>

        {/* Tabs + archive toggle */}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-b border-navy/10">
          <div className="flex gap-2">
            {[
              { key: "leads", label: `Leads (${leads.length})`, icon: Users },
              {
                key: "contacts",
                label: `Contacts (${contacts.length})`,
                icon: Mail,
              },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`admin-tab-${key}`}
                className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === key
                    ? "border-gold text-navy"
                    : "border-transparent text-ink/50 hover:text-navy"
                }`}
              >
                <Icon size={15} /> {label}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 pb-2 text-sm text-ink/60 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="admin-show-archived-toggle"
              className="accent-[#1B3A52] w-4 h-4"
            />
            Show archived ({archivedCount})
          </label>
        </div>

        {/* Table */}
        <div
          data-testid="admin-table-container"
          className="mt-6 bg-white border border-navy/10 overflow-x-auto"
        >
          {loading ? (
            <div className="p-12 flex items-center justify-center text-ink/50">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading…
            </div>
          ) : error ? (
            <div
              data-testid="admin-table-error"
              className="p-12 text-center text-red-500"
            >
              {error}
            </div>
          ) : rows.length === 0 ? (
            <div
              data-testid="admin-table-empty"
              className="p-12 text-center text-ink/50"
            >
              No {tab} to show. New submissions will appear here automatically.
            </div>
          ) : (
            <table
              data-testid="admin-data-table"
              className="w-full text-sm text-left"
            >
              <thead>
                <tr className="border-b border-navy/10 bg-navy/[0.03]">
                  {columns.map((c) => (
                    <th
                      key={c.key}
                      className="px-4 py-3 font-semibold text-navy whitespace-nowrap"
                    >
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 font-semibold text-navy whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const status = row.status || "new";
                  return (
                    <tr
                      key={row.id}
                      data-testid={`admin-row-${row.id}`}
                      className={`border-b border-navy/5 last:border-0 hover:bg-gold/5 align-top ${
                        status === "archived" ? "opacity-55" : ""
                      }`}
                    >
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className="px-4 py-3 text-ink/80 whitespace-pre-wrap break-words max-w-xs"
                        >
                          {c.key === "created_at" ? (
                            formatDate(row[c.key])
                          ) : c.key === "status" ? (
                            <StatusBadge status={status} />
                          ) : (
                            row[c.key] || "—"
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 whitespace-nowrap">
                        {updatingId === row.id ? (
                          <Loader2
                            size={15}
                            className="animate-spin text-ink/40"
                          />
                        ) : (
                          <div className="flex gap-2">
                            {status === "new" && (
                              <button
                                onClick={() =>
                                  setStatus(tab, row.id, "contacted")
                                }
                                data-testid={`row-action-contacted-${row.id}`}
                                className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:text-gold border border-navy/20 hover:border-gold px-2.5 py-1.5 transition-colors"
                              >
                                <Check size={13} /> Contacted
                              </button>
                            )}
                            {status !== "archived" ? (
                              <button
                                onClick={() =>
                                  setStatus(tab, row.id, "archived")
                                }
                                data-testid={`row-action-archive-${row.id}`}
                                className="inline-flex items-center gap-1 text-xs font-medium text-ink/50 hover:text-red-600 border border-ink/15 hover:border-red-300 px-2.5 py-1.5 transition-colors"
                              >
                                <Archive size={13} /> Archive
                              </button>
                            ) : (
                              <button
                                onClick={() => setStatus(tab, row.id, "new")}
                                data-testid={`row-action-restore-${row.id}`}
                                className="inline-flex items-center gap-1 text-xs font-medium text-ink/50 hover:text-navy border border-ink/15 hover:border-navy px-2.5 py-1.5 transition-colors"
                              >
                                <ArchiveRestore size={13} /> Restore
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
